import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, AlertCircle, CheckCircle, RefreshCw, Loader, TrendingUp } from 'lucide-react';
import bitdefenderService from '../services/bitdefenderService';
import EndpointSecurityCard from '../components/bitdefender/EndpointSecurityCard';
import { formatInCST } from '../utils/timezone';
import { toast } from 'sonner';

export default function BitdefenderDashboard() {
  const [endpoints, setEndpoints] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [unprotectedAssets, setUnprotectedAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [needsSync, setNeedsSync] = useState(false);

  useEffect(() => {
    loadData();
    loadUnprotectedAssets();
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [endpointsData, incidentsData] = await Promise.all([
        bitdefenderService.getEndpoints(),
        bitdefenderService.getIncidents()
      ]);
      
      if (endpointsData) {
        setEndpoints(endpointsData.endpoints || []);
        setNeedsSync(endpointsData.needs_sync || false);
      }
      
      if (incidentsData) {
        setIncidents(incidentsData.incidents || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load BitDefender data');
    } finally {
      setLoading(false);
    }
  };


  const loadUnprotectedAssets = async () => {
    try {
      const result = await bitdefenderService.getUnprotectedAssets();
      if (result && result.assets) {
        setUnprotectedAssets(result.assets);
      }
    } catch (error) {
      console.error('Error loading unprotected assets:', error);
    }
  };


  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await bitdefenderService.syncEndpoints();
      if (result.success) {
        toast.success('Endpoints synced successfully');
        await loadData();
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync endpoints');
    } finally {
      setSyncing(false);
    }
  };

  const stats = {
    total: endpoints.length,
    protected: endpoints.filter(e => e.security_status === 'protected').length,
    atRisk: endpoints.filter(e => e.security_status === 'at_risk').length,
    infected: endpoints.filter(e => e.security_status === 'infected').length,
    threats: endpoints.reduce((sum, e) => sum + (e.threat_count || 0), 0)
  };

  return (
    <div className="space-y-6 w-full p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            BitDefender Security Overview
          </h1>
          <p className="text-gray-600 mt-1">Manage endpoint protection across your organization</p>
        </div>
        <div className="flex gap-2">
          {needsSync && (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
              Sync Recommended
            </Badge>
          )}
          <Button 
            onClick={handleSync}
            disabled={syncing}
            className="gap-2"
          >
            {syncing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Endpoints</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Protected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{stats.protected}</p>
          </CardContent>
        </Card>

        <Card className="border-yellow-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              At Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-yellow-600">{stats.atRisk}</p>
          </CardContent>
        </Card>

        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Infected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{stats.infected}</p>
          </CardContent>
        </Card>

        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Active Threats</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{stats.threats}</p>
          </CardContent>
        </Card>

        <Card className="border-orange-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => window.location.href = '/assets?has_ninjarmm=true&has_bitdefender=false'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Unprotected Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-orange-600">{unprotectedAssets.length}</p>
            <p className="text-xs text-gray-500 mt-1">Devices without BitDefender</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Managed Endpoints</CardTitle>
            <CardDescription>Security status for all protected endpoints</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              <div>
                <TabsList className="inline-flex w-auto min-w-full lg:grid lg:grid-cols-4">
                  <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
                  <TabsTrigger value="protected">Protected ({stats.protected})</TabsTrigger>
                  <TabsTrigger value="risk">At Risk ({stats.atRisk})</TabsTrigger>
                  <TabsTrigger value="infected">Infected ({stats.infected})</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="space-y-4 mt-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin" />
                  </div>
                ) : endpoints.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No endpoints found</p>
                    <Button onClick={handleSync} className="mt-4" size="sm">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Endpoints
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {endpoints.map(endpoint => (
                      <EndpointSecurityCard
                        key={endpoint.endpoint_id}
                        endpointId={endpoint.endpoint_id}
                        endpointName={endpoint.endpoint_name}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="protected" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  {endpoints
                    .filter(e => e.security_status === 'protected')
                    .map(endpoint => (
                      <EndpointSecurityCard
                        key={endpoint.endpoint_id}
                        endpointId={endpoint.endpoint_id}
                        endpointName={endpoint.endpoint_name}
                      />
                    ))}
                  {endpoints.filter(e => e.security_status === 'protected').length === 0 && (
                    <p className="text-center text-gray-500 py-8">No protected endpoints</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="risk" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  {endpoints
                    .filter(e => e.security_status === 'at_risk')
                    .map(endpoint => (
                      <EndpointSecurityCard
                        key={endpoint.endpoint_id}
                        endpointId={endpoint.endpoint_id}
                        endpointName={endpoint.endpoint_name}
                      />
                    ))}
                  {endpoints.filter(e => e.security_status === 'at_risk').length === 0 && (
                    <p className="text-center text-gray-500 py-8">No at-risk endpoints</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="infected" className="space-y-4 mt-4">
                <div className="grid gap-4">
                  {endpoints
                    .filter(e => e.security_status === 'infected')
                    .map(endpoint => (
                      <EndpointSecurityCard
                        key={endpoint.endpoint_id}
                        endpointId={endpoint.endpoint_id}
                        endpointName={endpoint.endpoint_name}
                      />
                    ))}
                  {endpoints.filter(e => e.security_status === 'infected').length === 0 && (
                    <p className="text-center text-gray-500 py-8">No infected endpoints</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Security Incidents</CardTitle>
            <CardDescription>{incidents.length} total incidents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {incidents.slice(0, 10).map((incident) => (
                <div 
                  key={incident._id} 
                  className="flex items-start gap-3 p-3 bg-red-50 rounded border border-red-200"
                >
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-red-900">{incident.threat_name}</p>
                    <p className="text-xs text-red-800">{incident.endpoint_name}</p>
                    <p className="text-xs text-red-700">
                      {formatInCST(incident.detected_at, 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {incident.status}
                  </Badge>
                </div>
              ))}
              {incidents.length === 0 && (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-500">No recent incidents</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
